import { useState } from "react";
import {
	Box,
	Collapse,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Popover,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useEditTransactionMutation } from "../../../services/queryHooks";
import { useTheme } from "@mui/material/styles";
import ColorDisplayAndPicker from "../../../components/common/ColorDisplayAndPicker";
import { useTranslation } from "react-i18next";

export function CategorySelect({
	transaction,
	categories,
	setSelectedCategory,
	setSelectedSubcategory,
	selectedCategory,
	selectedSubcategory,
	anchorCategorySelect,
	setAnchorCategorySelect,
	isInModal,
}) {
	const theme = useTheme();
	const editTransactionMutation = useEditTransactionMutation();

	const [expandedCategory, setExpendedCategory] = useState(null);

	const { t } = useTranslation();

	const openCategory = (event, categoryId) => {
		event.stopPropagation();
		setExpendedCategory(categoryId);
	};

	const handleSelectCategory = (event, categoryId) => {
		event.stopPropagation();
		if (categoryId !== selectedCategory) {
			setExpendedCategory(categoryId);
			setSelectedCategory(categoryId);
			setSelectedSubcategory("");
		} else {
			setExpendedCategory(null);
			setSelectedCategory("");
		}
	};

	const handleSelectSubcategory = (event, categoryId, subcategoryId) => {
		event.stopPropagation();

		if (selectedCategory !== categoryId) {
			setSelectedCategory(categoryId);
		}
		setSelectedSubcategory(subcategoryId);
		if (!isInModal) {
			saveCategoryAndSubcategory(categoryId, subcategoryId);
		}
		setAnchorCategorySelect(null);
	};

	const saveCategoryAndSubcategory = (categoryId, subcategoryId) => {
		if (!categoryId) {
			setSelectedCategory(transaction?.categoryId);
			setSelectedSubcategory(transaction?.subcategoryId);
			return;
		}

		editTransactionMutation.mutate({
			transactionId: transaction._id,
			categoryId: categoryId,
			subcategoryId: subcategoryId,
		});
	};

	const handleClose = (event) => {
		event.stopPropagation();
		if (!isInModal) {
			saveCategoryAndSubcategory(selectedCategory, selectedSubcategory);
		}
		setAnchorCategorySelect(null);
	};

	const getBackgroundColor = (item, selectedItem) => {
		return selectedItem === item._id
			? theme.palette.background.default
			: theme.palette.background.paper;
	};

	const popoverStyles = {
		border: "1px solid",
		borderColor: "divider",
		borderRadius: "10px",
		width: "400px",
		px: 2,
	};

	const getCategoryListItemStyles = (category) => {
		return {
			borderRadius: "10px",
			py: 0.5,
			pl: 0.5,
			pr: 0,
			backgroundColor: getBackgroundColor(category, selectedCategory),
		};
	};

	const subcategoryListItemStyles = {
		display: "flex",
		flexDirection: "column",
		gap: 1,
		py: 0,
	};

	const getSubcategoryButtonStyles = (subcategory) => {
		return {
			display: "flex",
			alignItems: "center",
			py: 0,
			mx: 2,
			backgroundColor: getBackgroundColor(
				subcategory,
				selectedSubcategory
			),
			borderRadius: "10px",
		};
	};

	return (
		<Popover
			disableEnforceFocus
			open={Boolean(anchorCategorySelect)}
			anchorEl={anchorCategorySelect}
			onClose={(event) => handleClose(event)}
			anchorOrigin={{
				vertical: "bottom",
				horizontal: "center",
			}}
			transformOrigin={{
				vertical: "top",
				horizontal: "center",
			}}
			slotProps={{
				paper: {
					sx: popoverStyles,
				},
			}}
		>
			<List
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 1,
				}}
			>
				{categories.map((category) => (
					<Box
						key={category._id}
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 1,
						}}
					>
						<ListItem sx={getCategoryListItemStyles(category)}>
							<ListItemButton
								onClick={(event) =>
									handleSelectCategory(event, category._id)
								}
								sx={{
									borderRadius: "10px",
									py: 0.5,
								}}
							>
								<ColorDisplayAndPicker
									color={category.color}
									onColorChange={() => {}}
									isEditable={false}
								/>
								<ListItemText
									primary={
										category.groupName ||
										t(category.groupTranslationKey)
									}
								/>
							</ListItemButton>
							<ListItemIcon
								sx={{
									display: "flex",
									justifyContent: "center",
								}}
							>
								{category?.subcategories?.length !== 0 ? (
									expandedCategory === category._id ? (
										<ExpandLess
											sx={{
												cursor: "pointer",
											}}
											onClick={(event) =>
												openCategory(event, null)
											}
										/>
									) : (
										<ExpandMore
											sx={{ cursor: "pointer" }}
											onClick={(event) =>
												openCategory(
													event,
													category._id
												)
											}
										/>
									)
								) : null}
							</ListItemIcon>
						</ListItem>
						<Collapse
							in={expandedCategory === category._id}
							timeout="auto"
							unmountOnExit
						>
							<List sx={subcategoryListItemStyles}>
								{category?.subcategories.map((subcategory) => (
									<ListItemButton
										key={subcategory._id}
										sx={getSubcategoryButtonStyles(
											subcategory
										)}
									>
										<span
											style={{
												fontSize: 18,
												marginRight: 4,
											}}
										>
											{subcategory.emoji}
										</span>
										<ListItemText
											key={subcategory._id}
											onClick={(event) =>
												handleSelectSubcategory(
													event,
													category._id,
													subcategory._id
												)
											}
										>
											<ListItemText
												primary={
													subcategory.name ||
													t(
														subcategory.translationKey
													)
												}
											/>
										</ListItemText>
									</ListItemButton>
								))}
							</List>
						</Collapse>
					</Box>
				))}
			</List>
		</Popover>
	);
}
